"use client";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { calculateCosts, getAvailableFilaments } from "@/lib/price-calculator";
import {
  Center,
  ContactShadows,
  Environment,
  OrbitControls,
  PerspectiveCamera,
} from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ACESFilmicToneMapping, BufferGeometry, Vector3 } from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const COLOR_OPTIONS = {
  "#CCCCCC": "Light Gray",
  "#5A5A5A": "Gray",
  "#ffb95a": "Orange",
  "#5a9dff": "Blue",
  "#FF6B6B": "Red",
  "#6BCB77": "Green",
};

interface STLViewerProps {
  file: File;
  onPriceCalculated?: (price: number) => void;
  showPrice?: boolean;
}

function Model({
  file,
  onGeometryLoaded,
  color,
}: {
  file: File;
  onGeometryLoaded: (geometry: BufferGeometry) => void;
  color: string;
}) {
  const [model, setModel] = useState<{
    geometry: BufferGeometry;
    scale: number;
    originalGeometry: BufferGeometry;
  } | null>(null);

  useEffect(() => {
    if (!file) {
      setModel(null);
      return;
    }

    let isMounted = true;
    const reader = new FileReader();
    let geometry: BufferGeometry | null = null;

    reader.onload = (event) => {
      if (isMounted && event.target?.result) {
        try {
          const arrayBuffer = event.target.result as ArrayBuffer;
          const loader = new STLLoader();
          geometry = loader.parse(arrayBuffer);

          geometry.rotateX(-Math.PI / 2);
          geometry.computeBoundingBox();
          const originalGeometry = geometry.clone();

          let scale = 1;
          const bbox = geometry.boundingBox;
          if (bbox) {
            const size = new Vector3();
            bbox.getSize(size);
            const maxDimension = Math.max(size.x, size.y, size.z);
            scale = maxDimension > 1e-6 ? 1 / maxDimension : 1;
          }

          geometry.center();

          // Adjust position to sit on ground plane
          if (bbox && bbox.min && bbox.max) {
            geometry.translate(0, -bbox.min.y, 0);
          }

          if (isMounted) {
            setModel({ geometry, scale, originalGeometry });
            onGeometryLoaded(originalGeometry);
          }
        } catch (error) {
          console.error("Error loading model:", error);
          if (isMounted) setModel(null);
        }
      }
    };

    reader.onerror = (error) => {
      console.error("FileReader error:", error);
      if (isMounted) setModel(null);
    };

    reader.readAsArrayBuffer(file);

    return () => {
      isMounted = false;
      if (geometry) {
        geometry.dispose();
      }
      if (model?.originalGeometry) {
        model.originalGeometry.dispose();
      }
    };
  }, [file, onGeometryLoaded]);

  return (
    <Center>
      {model && (
        <mesh
          geometry={model.geometry}
          scale={[model.scale, model.scale, model.scale]}
        >
          <meshStandardMaterial
            key={color}
            color={color}
            metalness={0.15}
            roughness={0.5}
          />
        </mesh>
      )}
    </Center>
  );
}

export function STLViewer({
  file,
  onPriceCalculated,
  showPrice = false,
}: STLViewerProps) {
  const [priceDetails, setPriceDetails] = useState<ReturnType<
    typeof calculateCosts
  > | null>(null);
  const [currentGeometry, setCurrentGeometry] = useState<BufferGeometry | null>(
    null
  );
  const [selectedColor, setSelectedColor] = useState<string>("#CCCCCC");
  const [selectedFilament, setSelectedFilament] = useState<string>("PLA");

  const availableFilaments = useMemo(() => getAvailableFilaments(), []);

  useEffect(() => {
    if (currentGeometry) {
      try {
        const costs = calculateCosts(currentGeometry, selectedFilament);
        setPriceDetails(costs);
        if (onPriceCalculated) {
          onPriceCalculated(costs.totalCost);
        }
      } catch (error) {
        console.error("Error calculating costs:", error);
        setPriceDetails(null);
      }
    } else {
      setPriceDetails(null);
    }
  }, [currentGeometry, selectedFilament, onPriceCalculated]);

  const handleGeometryLoaded = useCallback((geometry: BufferGeometry) => {
    setCurrentGeometry(geometry);
  }, []);

  useEffect(() => {
    setPriceDetails(null);
    setCurrentGeometry(null);
  }, [file]);

  return (
    <div className="space-y-4">
      <div className="w-full h-[400px] bg-muted/50 rounded-lg overflow-hidden relative border shadow-sm">
        <Canvas
          className="absolute inset-0"
          gl={{
            antialias: true,
            toneMapping: ACESFilmicToneMapping,
            powerPreference: "high-performance",
            alpha: true,
          }}
          shadows
          dpr={[1, 2]}
        >
          <color attach="background" args={["#f9f9f9"]} />
          <Environment preset="apartment" />

          <PerspectiveCamera
            makeDefault
            fov={45}
            position={[0, 1, 2.5]}
            near={0.1}
            far={1000}
          />

          <ambientLight intensity={0.15} />
          <hemisphereLight
            color={0xffffff}
            groundColor={0x444444}
            intensity={0.6}
          />

          <directionalLight
            color={0xffffff}
            position={[3, 3, 3]}
            intensity={1.2}
            castShadow
            shadow-mapSize={[2048, 2048]}
          />
          <directionalLight
            color={0xccccff}
            position={[-1, 0.75, -0.5]}
            intensity={0.4}
          />

          <gridHelper
            args={[10, 20, "#111111", "#080808"]}
            position={[0, -0.01, 0]}
          />

          <ContactShadows
            frames={1}
            position={[0, -0.01, 0]}
            blur={2}
            opacity={0.4}
            far={1}
          />

          {file && (
            <Model
              file={file}
              onGeometryLoaded={handleGeometryLoaded}
              color={selectedColor}
            />
          )}

          <OrbitControls
            enableDamping
            dampingFactor={0.05}
            rotateSpeed={0.5}
            zoomSpeed={0.7}
            panSpeed={0.5}
            minDistance={0.1}
            maxDistance={10}
            target={[0, 0, 0]}
            minPolarAngle={Math.PI / 6}
            maxPolarAngle={Math.PI / 2.1}
          />
        </Canvas>
      </div>

      <div className="grid grid-cols-2 gap-4 p-4 border rounded-md bg-muted/20">
        <div>
          <Label htmlFor="stl-color-select" className="mb-2">
            Color
          </Label>
          <Select
            value={selectedColor}
            onValueChange={setSelectedColor}
            name="stl-color-select"
          >
            <SelectTrigger id="stl-color-select">
              <SelectValue placeholder="Select color" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(COLOR_OPTIONS).map(([hex, name]) => (
                <SelectItem key={hex} value={hex}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full border"
                      style={{ backgroundColor: hex }}
                    />
                    {name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="stl-filament-select" className="mb-2">
            Filament
          </Label>
          <Select
            value={selectedFilament}
            onValueChange={setSelectedFilament}
            name="stl-filament-select"
          >
            <SelectTrigger id="stl-filament-select">
              <SelectValue placeholder="Select filament" />
            </SelectTrigger>
            <SelectContent>
              {availableFilaments.map((filament) => (
                <SelectItem key={filament} value={filament}>
                  {filament}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {showPrice && priceDetails && (
        <Card>
          <CardHeader>
            <CardTitle>Price Estimate ({selectedFilament})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Material Volume:</span>
              <span>{priceDetails.materialVolume.toFixed(2)} cm³</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Material Cost:</span>
              <span>${priceDetails.materialCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Est. Machine Time:</span>
              <span>{priceDetails.machineTime.toFixed(1)} hours</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Labor Cost:</span>
              <span>${priceDetails.laborCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold border-t pt-2 mt-2">
              <span>Total Estimated Cost:</span>
              <span>${priceDetails.totalCost.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>
      )}
      {showPrice && !priceDetails && currentGeometry && file && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Calculating price for {selectedFilament}...
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
